import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
import { ANALYTICS_EVENT_NAMES } from '$lib/statics';
import { untrack } from 'svelte';
import type { Attachment } from 'svelte/attachments';

/**
 * Scroll-depth tracker, built as a factory instead of a bare attachment.
 *
 * Why it's shaped this way:
 * - Attachments are effects. Any reactive state read while setting one up (e.g. page.url via getRouteMetadata()) becomes a dependency, and when it changes Svelte tears the attachment down and re-runs it. Shallow routing (pushState / history.back) changes page.state, which caused spurious teardowns even though <main> never left the DOM.
 * - So nothing reactive is read at setup. Route metadata is read at send time, inside untrack(), so it always describes the page being left.
 * - flush() is exposed so +layout.svelte can call it from beforeNavigate for real page changes (the old accidental teardown [when using useBackButtonInterception()] used to do this job). Same-pathname navigations are skipped there, so back-button interactions don't send anything.
 * - visibilitychange covers tab close / app switch, where no navigation fires.
 */
export function createScrollTracker() {
    let maxScroll = 0;
    let analyticsApi: ClientVelatlasLoggerApi | undefined;

    function flush() {
        if (maxScroll === 0) return;

        // Read at send time; untracked so it can never create dependencies
        const metadata = untrack(() => ClientVelatlasLoggerApi.getRouteMetadata());
        analyticsApi ??= new ClientVelatlasLoggerApi();

        analyticsApi.sendAnalytics(
            {
                event: ANALYTICS_EVENT_NAMES.scrollDepth,
                payload: { maxPercentage: maxScroll },
                uri: metadata.uri,
                url: metadata.url
            },
            { keepalive: true }
        );

        maxScroll = 0;
    }

    const attach: Attachment<HTMLElement> = () => {
        if (typeof window === 'undefined') return;

        function handleScroll() {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollHeight =
                document.documentElement.scrollHeight - document.documentElement.clientHeight;
            if (scrollHeight <= 0) return;

            const percent = Math.round((scrollTop / scrollHeight) * 100);
            if (percent > maxScroll) maxScroll = Math.min(percent, 100);
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') flush();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            flush();
        };
    };

    return { attach, flush };
}